import React, { useState, useEffect } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import toast from "react-hot-toast";
import { LinkifiedText } from "../../../components/LinkifiedText";
import {
  useAcademyTopicsAdmin,
  useCreateAcademyTopic,
  useUpdateAcademyTopic,
  useDeleteAcademyTopic,
  useTopicProposals,
  useApproveProposal,
  useRejectProposal,
} from "../hooks/useTopics";

/** Muestra las dos descripciones de un tema (si tienen contenido), cada una
 * con su etiqueta de audiencia y enlaces clicables. Compartido entre la
 * tabla de escritorio y las tarjetas de móvil. */
function TopicDescriptions({ topic }) {
  if (!topic?.description_student && !topic?.description_teacher) return null;
  return (
    <div className="text-muted small mt-1 mb-1">
      {topic.description_student && (
        <div>
          <span className="fw-medium">Estudiantes: </span>
          <LinkifiedText text={topic.description_student} />
        </div>
      )}
      {topic.description_teacher && (
        <div>
          <span className="fw-medium">Docentes: </span>
          <LinkifiedText text={topic.description_teacher} />
        </div>
      )}
    </div>
  );
}

function TopicModal({ show, onHide, editing }) {
  const [name, setName] = useState("");
  const [descriptionStudent, setDescriptionStudent] = useState("");
  const [descriptionTeacher, setDescriptionTeacher] = useState("");
  const createMutation = useCreateAcademyTopic();
  const updateMutation = useUpdateAcademyTopic();
  const isEdit = Boolean(editing);
  const mutation = isEdit ? updateMutation : createMutation;

  useEffect(() => {
    if (isEdit) {
      setName(editing.topic?.name ?? "");
      setDescriptionStudent(editing.topic?.description_student ?? "");
      setDescriptionTeacher(editing.topic?.description_teacher ?? "");
    } else {
      setName("");
      setDescriptionStudent("");
      setDescriptionTeacher("");
    }
  }, [editing, show, isEdit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fields = {
      name,
      description_student: descriptionStudent,
      description_teacher: descriptionTeacher,
    };
    const payload = isEdit ? { id: editing.id, ...fields } : fields;
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(isEdit ? "Tema actualizado" : "Tema creado");
        onHide();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.field_errors?.name?.[0] ??
            err?.response?.data?.topic_id?.[0] ??
            err?.response?.data?.detail ??
            (isEdit ? "No se pudo actualizar el tema." : "No se pudo crear el tema.")
        ),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Editar tema" : "Nuevo tema"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">
              Nombre del tema <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Present Simple"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Descripción para estudiantes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={descriptionStudent}
              onChange={(e) => setDescriptionStudent(e.target.value)}
              placeholder="Lo que ve el estudiante. Puedes pegar enlaces (https://...)."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-medium">Descripción para docentes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={descriptionTeacher}
              onChange={(e) => setDescriptionTeacher(e.target.value)}
              placeholder="Instrucciones internas. Solo la ven docentes y tú."
            />
            <Form.Text className="text-muted">
              Los docentes ven esta descripción además de la de estudiantes.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : isEdit ? (
              "Guardar cambios"
            ) : (
              "Crear tema"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ProposalsSection() {
  const { data: proposals, isLoading } = useTopicProposals("PENDING");
  const approveMutation = useApproveProposal();
  const rejectMutation = useRejectProposal();

  const handleApprove = (p) => {
    approveMutation.mutate(p.id, {
      onSuccess: () => toast.success(`Tema "${p.name}" aprobado y agregado.`),
      onError: () => toast.error("No se pudo aprobar la propuesta."),
    });
  };

  const handleReject = (p) => {
    const note = window.prompt(
      `Rechazar "${p.name}". Motivo (opcional):`,
      ""
    );
    if (note === null) return; // cancelled
    rejectMutation.mutate(
      { id: p.id, review_note: note },
      {
        onSuccess: () => toast.success("Propuesta rechazada."),
        onError: () => toast.error("No se pudo rechazar la propuesta."),
      }
    );
  };

  const pending = proposals ?? [];

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Solicitudes de temas (docentes)</strong>
        {pending.length > 0 && <Badge bg="warning">{pending.length}</Badge>}
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {!isLoading && pending.length === 0 && (
          <p className="text-muted mb-0">No hay solicitudes pendientes.</p>
        )}
        {pending.map((p) => (
          <div
            key={p.id}
            className="d-flex justify-content-between align-items-start border-bottom py-2 gap-2 flex-wrap"
          >
            <div>
              <div className="fw-medium">{p.name}</div>
              {p.description && (
                <div className="text-muted small">{p.description}</div>
              )}
              {p.proposed_by_name && (
                <div className="text-muted small">
                  Propuesto por {p.proposed_by_name}
                </div>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                style={{ minHeight: 44 }}
                onClick={() => handleApprove(p)}
                disabled={approveMutation.isPending}
              >
                Aprobar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                style={{ minHeight: 44 }}
                onClick={() => handleReject(p)}
                disabled={rejectMutation.isPending}
              >
                Rechazar
              </Button>
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}

function TemasPage() {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: topics, isLoading, isError } = useAcademyTopicsAdmin();
  const updateMutation = useUpdateAcademyTopic();
  const deleteMutation = useDeleteAcademyTopic();

  const openNew = () => {
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleToggle = (t) => {
    updateMutation.mutate(
      { id: t.id, is_enabled: !t.is_enabled },
      {
        onError: () => toast.error("No se pudo actualizar el tema."),
      }
    );
  };

  const handleDelete = (t) => {
    if (!window.confirm(`¿Eliminar el tema "${t.topic?.name}" del catálogo?`))
      return;
    deleteMutation.mutate(t.id, {
      onSuccess: () => toast.success("Tema eliminado"),
      onError: () => toast.error("No se pudo eliminar el tema."),
    });
  };

  return (
    <>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <h4 className="mb-0">Temas</h4>
          <Button
            variant="primary"
            style={{ minHeight: 44 }}
            onClick={openNew}
          >
            + Nuevo tema
          </Button>
        </div>
        <p className="text-muted small mb-3">
          Administra el catálogo de temas de tu academia. Solo los temas
          habilitados pueden ser usados por los docentes al planear sus clases.
        </p>

        <ProposalsSection />

        {isLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {isError && (
          <Alert variant="danger">No se pudieron cargar los temas.</Alert>
        )}

        {!isLoading && !isError && (topics ?? []).length === 0 && (
          <Card>
            <Card.Body className="text-center text-muted py-4">
              Aún no hay temas en el catálogo.
            </Card.Body>
          </Card>
        )}

        {!isLoading && !isError && (topics ?? []).length > 0 && (
          <>
            {/* PC: tabla */}
            <Card className="d-none d-md-block">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Tema</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topics ?? []).map((t) => (
                      <tr key={t.id}>
                        <td>
                          <div className="fw-medium">{t.topic?.name}</div>
                          <TopicDescriptions topic={t.topic} />
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`topic-${t.id}`}
                            checked={Boolean(t.is_enabled)}
                            onChange={() => handleToggle(t)}
                            label={t.is_enabled ? "Habilitado" : "Deshabilitado"}
                            disabled={updateMutation.isPending}
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              style={{ minHeight: 44 }}
                              onClick={() => openEdit(t)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ minHeight: 44 }}
                              onClick={() => handleDelete(t)}
                              disabled={deleteMutation.isPending}
                            >
                              Eliminar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>

            {/* Móvil: tarjetas */}
            <div className="d-md-none d-flex flex-column gap-2">
              {(topics ?? []).map((t) => (
                <Card key={t.id}>
                  <Card.Body className="py-2 px-3">
                    <div className="fw-medium">{t.topic?.name}</div>
                    <TopicDescriptions topic={t.topic} />
                    <Form.Check
                      type="switch"
                      id={`topic-m-${t.id}`}
                      checked={Boolean(t.is_enabled)}
                      onChange={() => handleToggle(t)}
                      label={t.is_enabled ? "Habilitado" : "Deshabilitado"}
                      disabled={updateMutation.isPending}
                      className="my-2"
                    />
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-fill"
                        style={{ minHeight: 44 }}
                        onClick={() => openEdit(t)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="flex-fill"
                        style={{ minHeight: 44 }}
                        onClick={() => handleDelete(t)}
                        disabled={deleteMutation.isPending}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      <TopicModal show={showModal} onHide={closeModal} editing={editing} />
    </>
  );
}

export { TemasPage };
